import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CITATION_PLACEHOLDER_MARKER_PATTERN,
  CITATION_RAW_MARKER_PATTERN,
  WEB_CITATION_PLACEHOLDER_MARKER_PATTERN,
  WEB_CITATION_RAW_MARKER_PATTERN,
  citationPlaceholderBody,
  parseCitationMarker,
} from './citation-utils';
import { settledMarkdownLength, settledRevealLength } from './markdown-blocks';

const STREAM_REVEAL_INTERVAL_MS = 16;
// How long a piece of text would like to take to type, whatever its length.
// Everything is held server-side until its round is classified, so it lands
// complete and the reveal is free to choose: a line of commentary spread over
// about a second reads as writing, where the same line at answer speed is over
// before the eye catches it.
const STREAM_REVEAL_TARGET_MS = 1000;
// Below this the text is too short to be worth a full second — a few words
// creeping out one at a time reads as a stall, not as typing.
const STREAM_REVEAL_MIN_CHARS_PER_SECOND = 120;
// And above this a long answer would take longer to appear than to read.
const STREAM_REVEAL_MAX_CHARS_PER_SECOND = 920;
const STREAM_REVEAL_MAX_FRAME_DELTA_MS = 48;
const COMPLETE_CITATION_PATTERN = new RegExp(
  `(?:${CITATION_RAW_MARKER_PATTERN}|${WEB_CITATION_RAW_MARKER_PATTERN})`,
  'gi',
);
const COMPLETE_RAW_CITATION_AT_START = new RegExp(
  `^(?:${CITATION_RAW_MARKER_PATTERN}|${WEB_CITATION_RAW_MARKER_PATTERN})$`,
  'i',
);
const MALFORMED_CITATION_PATTERN = /\[(?:cite|web):[^\]]+\]/gi;
const INCOMPLETE_CITATION_PATTERN = /\[(?:c(?:i(?:t(?:e(?::[^\]]*)?)?)?)?|w(?:e(?:b(?::[^\]]*)?)?)?)?$/i;
const RAW_CITATION_PREFIXES = ['[cite:', '[web:'] as const;
const CITATION_PLACEHOLDER_PREFIX = '@@crimson-cite:';
const WEB_CITATION_PLACEHOLDER_PREFIX = '@@crimson-web:';
const CITATION_PLACEHOLDER_AT_START = new RegExp(
  `^(?:${CITATION_PLACEHOLDER_MARKER_PATTERN}|${WEB_CITATION_PLACEHOLDER_MARKER_PATTERN})`,
  'i',
);
const CITATION_CLUSTER_SEPARATOR_AT_START = /^[^\S\r\n]*(?:[,;][^\S\r\n]*)?/;
const CITATION_CLUSTER_SEPARATOR_AT_END = /(?:[^\S\r\n]*[,;])?[^\S\r\n]*$/;

interface SmoothStreamingContentOptions {
  continuityKey?: string;
  startWithExistingContent?: boolean;
  /** Drop the reveal cap, e.g. once the user has asked to stop the turn. */
  revealImmediately?: boolean;
}

/**
 * How fast to type a piece of text of this length, in characters per second.
 *
 * The pace comes from the whole of what is being written, not from what is
 * left of it, so it is the same on the last character as on the first. Setting
 * it from the remainder instead makes every piece of text start with a lump
 * and trail off, which is the shape of a dump rather than of writing.
 *
 * Between the two bounds the length decides: aiming everything at roughly the
 * same duration makes a sentence of commentary and a page of answer feel like
 * the same hand at work, one just having more to say.
 */
export function streamRevealCharsPerSecond(totalChars: number): number {
  return Math.min(
    STREAM_REVEAL_MAX_CHARS_PER_SECOND,
    Math.max(
      STREAM_REVEAL_MIN_CHARS_PER_SECOND,
      (totalChars * 1000) / STREAM_REVEAL_TARGET_MS,
    ),
  );
}

/**
 * Characters to reveal on this frame. Revealing immediately keeps the visible
 * text level with the text that will be committed to the transcript.
 */
export function streamRevealCharBudget(
  frameDeltaMs: number,
  remaining: number,
  revealImmediately: boolean,
  totalChars: number,
): number {
  if (revealImmediately) {
    return remaining;
  }

  const frameDurationMs = Math.max(
    Math.min(frameDeltaMs, STREAM_REVEAL_MAX_FRAME_DELTA_MS),
    STREAM_REVEAL_INTERVAL_MS,
  );
  const frameBudget = Math.max(
    1,
    Math.round((frameDurationMs / 1000) * streamRevealCharsPerSecond(totalChars)),
  );

  return Math.min(remaining, frameBudget);
}

export function shouldSnapStreamingCatchUp(
  currentDisplay: string,
  targetContent: string,
  startWithExistingContent: boolean,
): boolean {
  return startWithExistingContent && currentDisplay.length === 0 && targetContent.length > 0;
}

/** Keep typing through a continuity-key change instead of snapping to the latest target. */
export function shouldPreserveStreamingDisplay(
  currentDisplay: string,
  targetContent: string,
): boolean {
  if (currentDisplay.length === 0) return false;
  return targetContent.startsWith(currentDisplay)
    || currentDisplay.startsWith(targetContent);
}

/** Visible text that catch-up already painted, longer than the live held target. */
export function isStreamingDisplayAheadOfTarget(
  currentDisplay: string,
  targetContent: string,
): boolean {
  return currentDisplay.length > 0
    && currentDisplay.startsWith(targetContent)
    && currentDisplay !== targetContent;
}

export function resolveInitialStreamingDisplay({
  cachedContent,
  currentDisplay = '',
  startWithExistingContent,
  targetContent,
}: {
  cachedContent?: string;
  /** Text already painted for this block, if any. Empty on a first mount. */
  currentDisplay?: string;
  startWithExistingContent: boolean;
  targetContent: string;
}): string {
  if (cachedContent && targetContent.startsWith(cachedContent)) {
    return cachedContent;
  }
  if (startWithExistingContent && targetContent.length > 0) {
    return targetContent;
  }
  // A block the reader is already looking at must never blank. Reaching here
  // with visible text means the turn re-keyed or re-split mid-flight, which is
  // the same block under a new identity, so catch up instead of re-typing.
  if (currentDisplay.length > 0) {
    return targetContent;
  }
  return '';
}

/** Freeze the streaming cache key for the duration of a live turn. */
export function freezeStreamingContinuityKey(
  currentKey: string,
  nextKey: string,
  isStreaming: boolean,
): string {
  if (isStreaming && currentKey) {
    return currentKey;
  }
  return nextKey;
}

// What a turn had painted when it last unmounted, so a remount mid-stream
// resumes instead of flashing empty. A turn's entry is only cleared when it
// renders empty, so without a bound a long session accumulates one string per
// turn forever. Only the turns near the bottom of the thread can remount into
// a live stream, so evicting the oldest costs nothing.
const STREAM_CONTINUITY_CACHE_LIMIT = 32;
const streamContinuityCache = new Map<string, string>();

function rememberStreamContinuity(key: string, content: string): void {
  // Re-inserting makes the Map's insertion order a recency order.
  streamContinuityCache.delete(key);
  streamContinuityCache.set(key, content);
  while (streamContinuityCache.size > STREAM_CONTINUITY_CACHE_LIMIT) {
    const [oldest] = streamContinuityCache.keys();
    streamContinuityCache.delete(oldest);
  }
}

/**
 * Prepare raw streamed model output for live markdown rendering.
 *
 * `holdUnsettledTail` keeps back the end of a live answer until it means what
 * it will finally mean — a half-written citation, a run of pipes that has not
 * become a table yet. It is off once the answer is final or the lawyer has
 * asked to stop, so what is on screen at that point is the whole of what gets
 * committed to the transcript.
 */
export function processStreamingContent(
  streamingContent: string | null,
  hideCitations: boolean = false,
  holdUnsettledTail: boolean = false
): string {
  if (!streamingContent) return '';

  // Remove Citations section - match multiple patterns
  let content = streamingContent
    .replace(/##\s*Citations\s*\n[\s\S]*$/i, '')
    .replace(/##\s*Citations\s*$/i, '')
    .replace(/##\s*Citat[^\n]*$/i, '');

  if (holdUnsettledTail && !hideCitations) {
    const trailingCitationClusterStart = getTrailingRawCitationClusterStart(content);
    if (trailingCitationClusterStart !== null) {
      content = content.substring(0, trailingCitationClusterStart);
    }
  }

  if (hideCitations) {
    content = content
      .replace(COMPLETE_CITATION_PATTERN, '')
      .replace(MALFORMED_CITATION_PATTERN, '')
      .replace(INCOMPLETE_CITATION_PATTERN, '');
  } else {
    content = content
      .replace(COMPLETE_CITATION_PATTERN, (marker) => {
        const citation = parseCitationMarker(marker);
        if (!citation) return '';
        if (citation.kind === 'web') {
          return `@@crimson-web:${citationPlaceholderBody(citation)}@@`;
        }
        return `${CITATION_PLACEHOLDER_PREFIX}${citationPlaceholderBody(citation)}@@`;
      })
      .replace(MALFORMED_CITATION_PATTERN, '')
      .replace(INCOMPLETE_CITATION_PATTERN, '');
  }

  const collapsed = collapseInlineWhitespacePreservingIndentation(content);

  return holdUnsettledTail
    ? collapsed.slice(0, settledMarkdownLength(collapsed))
    : collapsed;
}

function collapseInlineWhitespacePreservingIndentation(content: string) {
  return content.replace(
    /^([^\S\r\n]*)(.*)$/gm,
    (_line, indentation: string, body: string) =>
      `${indentation}${body.replace(/[^\S\r\n]{2,}/g, ' ')}`
  );
}

/**
 * Smoothly reveals streamed content so network chunking does not directly drive UI paint.
 *
 * `revealed` reports whether the paced reveal has caught up with everything
 * received so far. Callers that sequence other UI against the text need it,
 * because content arriving is not the same moment as content being on screen.
 */
export function useSmoothStreamingReveal(
  streamingContent: string,
  isStreaming: boolean,
  hideCitations: boolean = false,
  options: SmoothStreamingContentOptions = {}
): { content: string; revealed: boolean } {
  const { continuityKey, startWithExistingContent = false, revealImmediately = false } = options;
  const targetContent = useMemo(
    () => processStreamingContent(streamingContent, hideCitations, isStreaming && !revealImmediately),
    [streamingContent, hideCitations, isStreaming, revealImmediately]
  );
  const getInitialDisplayContent = () => {
    const cachedContent = continuityKey
      ? streamContinuityCache.get(continuityKey)
      : undefined;
    return resolveInitialStreamingDisplay({
      cachedContent,
      startWithExistingContent,
      targetContent,
    });
  };
  const [displayContent, setDisplayContent] = useState(getInitialDisplayContent);
  const targetRef = useRef(targetContent);
  targetRef.current = targetContent;
  const displayRef = useRef(displayContent);
  const revealImmediatelyRef = useRef(revealImmediately);
  revealImmediatelyRef.current = revealImmediately;
  const frameRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFrameAtRef = useRef(0);

  const setDisplayContentWithCache = useCallback((nextContent: string) => {
    displayRef.current = nextContent;
    setDisplayContent(nextContent);

    if (continuityKey) {
      if (nextContent) {
        rememberStreamContinuity(continuityKey, nextContent);
      } else {
        streamContinuityCache.delete(continuityKey);
      }
    }
  }, [continuityKey]);

  const cancelScheduledFrame = useCallback(() => {
    if (frameRef.current !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isStreaming) {
      if (shouldPreserveStreamingDisplay(displayRef.current, targetRef.current)) {
        if (continuityKey) {
          rememberStreamContinuity(continuityKey, displayRef.current);
        }
        return;
      }

      const cachedContent = continuityKey
        ? streamContinuityCache.get(continuityKey)
        : undefined;
      const nextDisplayContent = resolveInitialStreamingDisplay({
        cachedContent,
        currentDisplay: displayRef.current,
        startWithExistingContent,
        targetContent: targetRef.current,
      });

      setDisplayContentWithCache(nextDisplayContent);
      lastFrameAtRef.current = 0;
      return;
    }

    if (!targetRef.current) {
      setDisplayContentWithCache('');
    }
  }, [continuityKey, isStreaming, setDisplayContentWithCache, startWithExistingContent]);

  useEffect(() => {
    if (targetRef.current !== targetContent) {
      targetRef.current = targetContent;
    }

    const scheduleFrame = (delay = 0) => {
      if (frameRef.current !== null || timeoutRef.current !== null) {
        return;
      }

      const requestFrame = () => {
        if (typeof requestAnimationFrame === 'function') {
          frameRef.current = requestAnimationFrame(tick);
          return;
        }

        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          tick(Date.now());
        }, STREAM_REVEAL_INTERVAL_MS);
      };

      if (delay > 0) {
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          requestFrame();
        }, delay);
        return;
      }

      requestFrame();
    };

    const tick = (now: number) => {
      frameRef.current = null;

      const target = targetRef.current;
      const current = displayRef.current;

      if (shouldSnapStreamingCatchUp(current, target, startWithExistingContent)) {
        setDisplayContentWithCache(target);
        lastFrameAtRef.current = now;
        return;
      }

      if (!target.startsWith(current)) {
        // Catch-up paints the unheld tail (an open table, a bare heading).
        // Live then holds that tail back, so the target shrinks. Snapping to
        // it would delete rows the lawyer already read and retype them.
        if (isStreamingDisplayAheadOfTarget(current, target)) {
          lastFrameAtRef.current = now;
          return;
        }
        setDisplayContentWithCache(target);
        lastFrameAtRef.current = now;
        return;
      }

      const remaining = target.length - current.length;
      if (remaining <= 0) {
        lastFrameAtRef.current = now;
        return;
      }

      const frameDeltaMs = lastFrameAtRef.current
        ? now - lastFrameAtRef.current
        : STREAM_REVEAL_INTERVAL_MS;
      const visibleBudget = streamRevealCharBudget(
        frameDeltaMs,
        remaining,
        revealImmediatelyRef.current,
        target.length,
      );
      const nextLength = settledRevealLength(
        target,
        current.length,
        getCitationAwareRevealLength(target, current.length, visibleBudget),
      );
      const nextContent = target.slice(0, nextLength);

      setDisplayContentWithCache(nextContent);
      lastFrameAtRef.current = now;

      if (nextLength < target.length) {
        scheduleFrame();
      }
    };

    scheduleFrame();
  }, [setDisplayContentWithCache, startWithExistingContent, targetContent]);

  useEffect(() => cancelScheduledFrame, [cancelScheduledFrame]);

  return {
    content: displayContent,
    revealed: displayContent === targetContent
      || isStreamingDisplayAheadOfTarget(displayContent, targetContent),
  };
}

export function useSmoothStreamingContent(
  streamingContent: string,
  isStreaming: boolean,
  hideCitations: boolean = false,
  options: SmoothStreamingContentOptions = {}
): string {
  return useSmoothStreamingReveal(streamingContent, isStreaming, hideCitations, options).content;
}

function nextCitationPlaceholderStart(target: string, from: number): number {
  const citeStart = target.indexOf(CITATION_PLACEHOLDER_PREFIX, from);
  const webStart = target.indexOf(WEB_CITATION_PLACEHOLDER_PREFIX, from);
  if (citeStart === -1) return webStart;
  if (webStart === -1) return citeStart;
  return Math.min(citeStart, webStart);
}

function isCitationPlaceholderAt(target: string, index: number): boolean {
  return target.startsWith(CITATION_PLACEHOLDER_PREFIX, index)
    || target.startsWith(WEB_CITATION_PLACEHOLDER_PREFIX, index);
}

export function getCitationAwareRevealLength(
  target: string,
  startLength: number,
  visibleBudget: number
): number {
  let nextLength = startLength;
  let remainingVisibleBudget = visibleBudget;

  while (nextLength < target.length) {
    const citationClusterEnd = getCitationPlaceholderClusterEnd(target, nextLength);
    if (citationClusterEnd !== null) {
      nextLength = citationClusterEnd;
      continue;
    }

    if (remainingVisibleBudget <= 0) {
      break;
    }

    const nextCitationStart = nextCitationPlaceholderStart(target, nextLength);
    const visibleRunEnd = nextCitationStart === -1 ? target.length : nextCitationStart;
    const visibleRunLength = visibleRunEnd - nextLength;
    const visibleCharsToReveal = Math.min(remainingVisibleBudget, visibleRunLength);

    nextLength += visibleCharsToReveal;
    remainingVisibleBudget -= visibleCharsToReveal;

    if (nextLength < visibleRunEnd) {
      break;
    }
  }

  return nextLength;
}

function getCitationPlaceholderClusterEnd(target: string, citationStart: number): number | null {
  if (!isCitationPlaceholderAt(target, citationStart)) {
    return null;
  }

  const firstMarker = target.slice(citationStart).match(CITATION_PLACEHOLDER_AT_START);
  if (!firstMarker) {
    return null;
  }

  let clusterEnd = citationStart + firstMarker[0].length;

  while (clusterEnd < target.length) {
    const separator = getLeadingCitationClusterSeparator(target.slice(clusterEnd));
    const nextCitationStart = clusterEnd + separator.length;
    const nextMarker = target.slice(nextCitationStart).match(CITATION_PLACEHOLDER_AT_START);
    if (!nextMarker) {
      break;
    }

    clusterEnd = nextCitationStart + nextMarker[0].length;
  }

  return clusterEnd;
}

function getLeadingCitationClusterSeparator(content: string): string {
  return content.match(CITATION_CLUSTER_SEPARATOR_AT_START)?.[0] ?? '';
}

function getTrailingRawCitationClusterStart(content: string): number | null {
  const trailingIncompleteCitation = content.match(INCOMPLETE_CITATION_PATTERN)?.[0];
  if (trailingIncompleteCitation) {
    const incompleteCitationStart = content.length - trailingIncompleteCitation.length;
    const clusterStart = getRawCitationClusterStartBefore(content.slice(0, incompleteCitationStart));

    return clusterStart ?? incompleteCitationStart;
  }

  return getRawCitationClusterStartBefore(content);
}

function getRawCitationClusterStartBefore(content: string): number | null {
  const trailingSeparator = getTrailingCitationClusterSeparator(content);
  const clusterEnd = content.length - trailingSeparator.length;
  if (clusterEnd <= 0) return null;

  const lastCitationStart = lastRawCitationStart(content, clusterEnd - 1);
  if (lastCitationStart === -1) return null;

  const lastCitation = content.slice(lastCitationStart, clusterEnd);
  if (!COMPLETE_RAW_CITATION_AT_START.test(lastCitation)) return null;

  return getRawCitationClusterStart(content, lastCitationStart);
}

function getRawCitationClusterStart(content: string, citationStart: number): number {
  let clusterStart = citationStart;

  while (clusterStart > 0) {
    const precedingSeparator = getTrailingCitationClusterSeparator(content.slice(0, clusterStart));
    const previousCitationEnd = clusterStart - precedingSeparator.length;
    if (previousCitationEnd <= 0) {
      break;
    }

    const previousCitationStart = lastRawCitationStart(content, previousCitationEnd - 1);
    if (previousCitationStart === -1) {
      break;
    }

    const previousCitation = content.slice(previousCitationStart, previousCitationEnd);
    if (!COMPLETE_RAW_CITATION_AT_START.test(previousCitation)) {
      break;
    }

    clusterStart = previousCitationStart;
  }

  return clusterStart;
}

function lastRawCitationStart(content: string, fromIndex: number): number {
  let found = -1;
  for (const prefix of RAW_CITATION_PREFIXES) {
    const index = content.lastIndexOf(prefix, fromIndex);
    if (index > found) found = index;
  }
  return found;
}

function getTrailingCitationClusterSeparator(content: string): string {
  return content.match(CITATION_CLUSTER_SEPARATOR_AT_END)?.[0] ?? '';
}
